/**
 * Backward-move reason gate.
 *
 * Shown when an operations Kanban card is dragged BACKWARD to an earlier stage.
 * Moving a card back (undoing progress) requires a written reason: it is stored,
 * together with the acting user, in the append-only quote_operations_audit_logs
 * (see migration 0287) so the "why" behind every regression is permanently
 * traceable and cannot be edited or deleted.
 *
 * The move is blocked until a non-empty reason is entered — the confirm button
 * stays disabled while the field is blank.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

export interface BackwardMoveReasonDialogProps {
  open: boolean;
  quoteNumber: string | null | undefined;
  fromStageLabel: string;
  toStageLabel: string;
  pending?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function BackwardMoveReasonDialog({
  open,
  quoteNumber,
  fromStageLabel,
  toStageLabel,
  pending = false,
  onConfirm,
  onCancel,
}: BackwardMoveReasonDialogProps) {
  const { t } = useTranslation('operations');
  const [reason, setReason] = useState('');
  const initializedRef = useRef(false);

  // Reset the field each time the dialog opens so a prior reason never leaks
  // into a different card's move.
  useEffect(() => {
    if (open && !initializedRef.current) {
      setReason('');
      initializedRef.current = true;
    }
    if (!open) initializedRef.current = false;
  }, [open]);

  const trimmed = reason.trim();
  const canConfirm = trimmed.length > 0 && !pending;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('backwardMove.title')}</DialogTitle>
          <DialogDescription>
            {t('backwardMove.description', {
              quoteNumber: quoteNumber || '—',
              fromStage: fromStageLabel,
              toStage: toStageLabel,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="backward-move-reason">
            {t('backwardMove.reasonLabel')}
          </Label>
          <Textarea
            id="backward-move-reason"
            autoFocus
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('backwardMove.reasonPlaceholder')}
          />
          <p className="text-xs text-muted-foreground">{t('backwardMove.auditNote')}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            {t('backwardMove.cancel')}
          </Button>
          <Button onClick={() => onConfirm(trimmed)} disabled={!canConfirm}>
            {pending ? t('backwardMove.confirming') : t('backwardMove.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
